// Top-level screens: loading -> auth -> menu <-> game.
// Inside Discord (a Discord Activity): loading (sign in through Discord) ->
// the voice channel's shared table, or the menu; never the login screen.
import { Suspense, lazy, useCallback, useEffect, useRef, useState } from "react";
import type { Me } from "@wp/shared";
import { ACTIVITY, IN_DISCORD } from "./discord/activity";
import type { ActivitySignInResult } from "./discord/discordSdk";
import { loadActiveLobby, saveActiveLobby, takeAuthError } from "./lib/session";
import { api } from "./net/api";
import { blips } from "./settings/audio";
import { music } from "./settings/music";
import { useSettings } from "./settings/SettingsContext";
import { RefillNotice } from "./ui/common/RefillNotice";
import { TeamNotices } from "./ui/common/TeamNotices";
import { LegalPage, legalPageFor } from "./ui/legal/LegalPage";
import { AuthScreen } from "./ui/menu/AuthScreen";
import { WelcomeScreen } from "./ui/menu/WelcomeScreen";

// The game screen pulls in three.js; load it only when entering a lobby.
const GameScreen = lazy(() => import("./ui/game/GameScreen").then((m) => ({ default: m.GameScreen })));

/** Shown on the login screen when a lobby socket was closed because the session ended. */
export const SESSION_ENDED_MESSAGE = "Your session has ended. Please log in again.";

type Screen =
  | { kind: "loading" }
  | { kind: "offline"; message: string }
  | { kind: "auth" }
  /** Discord Activity: signing in through Discord failed or the session ended. */
  | { kind: "discord"; message: string }
  | { kind: "menu" }
  | { kind: "game"; lobbyId: string };

function Splash({ text }: { text: string }) {
  return (
    <div className="splash" aria-busy="true">
      <span className="logo-mark spin" aria-hidden="true">
        ✦
      </span>
      <p>{text}</p>
    </div>
  );
}

function initialAuthError(): string | null {
  const { message, cleanUrl } = takeAuthError(window.location.href);
  if (cleanUrl !== null) window.history.replaceState(null, "", cleanUrl);
  return message;
}

export function App() {
  // /terms and /privacy are plain pages: no login needed.
  const legal = legalPageFor(window.location.pathname);
  if (legal) return <LegalPage kind={legal} />;
  return <Game />;
}

/** The current screen, plus notices that may show over any of them. */
function Game() {
  const [me, setMe] = useState<Me | null>(null);
  return (
    <>
      <Screens me={me} setMe={setMe} />
      <RefillNotice me={me} />
      <TeamNotices userId={me?.userId ?? null} />
    </>
  );
}

/** A soft click whenever a UI button is pressed (one listener for the whole page). */
function useButtonSounds(): void {
  const { settings } = useSettings();
  const audio = useRef(settings.audio);
  audio.current = settings.audio;
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const button = (e.target as Element | null)?.closest?.("button");
      if (button && !button.disabled) blips.play("button", audio.current);
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}

/**
 * Background music in the main menu and at the tables (one shared track that
 * keeps playing between them); stopped on the login screen.
 */
function useMusic(active: boolean): void {
  const { settings } = useSettings();
  const { master, music: musicLevel, effects } = settings.audio;
  useEffect(() => {
    music.setVolume({ master, music: musicLevel, effects });
  }, [master, musicLevel, effects]);
  useEffect(() => {
    if (active) music.setActive(true);
    else music.stop();
  }, [active]);
}

function Screens({ me, setMe }: { me: Me | null; setMe: (me: Me | null) => void }) {
  useButtonSounds();
  const [screen, setScreen] = useState<Screen>({ kind: "loading" });
  useMusic(screen.kind === "menu" || screen.kind === "game");
  const [notice, setNotice] = useState<string | null>(null);
  const [authError, setAuthError] = useState(initialAuthError);

  /** Discord Activity: pass for this instance's shared table (null when none). */
  const [instancePass, setInstancePass] = useState<string | null>(null);
  const booted = useRef(false);

  /** Discord Activity: sit at the table everyone in this Activity instance shares. Null on success, else why not. */
  const joinVoiceTable = useCallback(async (pass: string): Promise<string | null> => {
    if (!ACTIVITY) return "Not running in Discord.";
    const res = await api.activityLobby(ACTIVITY.instanceId, pass);
    if (!res.ok) return res.error.message;
    saveActiveLobby(res.value.id);
    setNotice(null);
    setScreen({ kind: "game", lobbyId: res.value.id });
    return null;
  }, []);

  /** Discord Activity start-up: sign in through Discord (no login screen), then go to the shared table. */
  const signInDiscord = useCallback(async () => {
    if (!ACTIVITY) return;
    setScreen({ kind: "loading" });
    let result: ActivitySignInResult;
    try {
      const { signInWithDiscord } = await import("./discord/discordSdk");
      result = await signInWithDiscord(ACTIVITY);
    } catch {
      result = { ok: false, message: "Can't reach the server. Check your connection and try again." };
    }
    if (!result.ok) {
      setScreen({ kind: "discord", message: result.message });
      return;
    }
    setMe(result.value.me);
    setInstancePass(result.value.instancePass);
    if (result.value.instancePass) {
      const problem = await joinVoiceTable(result.value.instancePass);
      if (problem === null) return;
      setNotice(`Couldn't seat you at your voice channel's table: ${problem}`);
      setScreen({ kind: "menu" });
      return;
    }
    const lobbyId = loadActiveLobby();
    setScreen(lobbyId ? { kind: "game", lobbyId } : { kind: "menu" });
  }, [joinVoiceTable]);

  const loadMe = useCallback(async () => {
    setScreen({ kind: "loading" });
    const res = await api.me();
    if (res.ok) {
      setMe(res.value);
      const lobbyId = loadActiveLobby();
      setScreen(lobbyId ? { kind: "game", lobbyId } : { kind: "menu" });
    } else if (res.error.status === 401) {
      setScreen({ kind: "auth" });
    } else {
      setScreen({ kind: "offline", message: res.error.message });
    }
  }, []);

  useEffect(() => {
    if (!ACTIVITY) {
      void loadMe();
    } else if (!booted.current) {
      // Once per page (StrictMode runs effects twice): authorize() may show Discord's consent dialog.
      booted.current = true;
      void signInDiscord();
    }
  }, [loadMe, signInDiscord]);

  const enterLobby = useCallback((lobbyId: string) => {
    saveActiveLobby(lobbyId);
    setNotice(null);
    setScreen({ kind: "game", lobbyId });
  }, []);

  const exitLobby = useCallback((message: string | null) => {
    saveActiveLobby(null);
    setNotice(message);
    setScreen({ kind: "menu" });
  }, []);

  const onAuthed = useCallback((m: Me) => {
    setMe(m);
    setScreen({ kind: "menu" });
  }, []);

  const onLogout = useCallback(() => {
    saveActiveLobby(null);
    setMe(null);
    if (IN_DISCORD) {
      // Only after deleting the account (there is no logout inside Discord).
      api.setSessionToken(null);
      setScreen({ kind: "discord", message: "Your account was deleted." });
      return;
    }
    setScreen({ kind: "auth" });
  }, []);

  // The session expired or was logged out elsewhere: back to the login screen
  // with the reason, from the menu (a REST call got 401) or a lobby (close 4401).
  // Inside Discord there is no login screen: offer to sign in through Discord again.
  const onSignedOut = useCallback((message: string = SESSION_ENDED_MESSAGE) => {
    saveActiveLobby(null);
    setMe(null);
    if (IN_DISCORD) {
      api.setSessionToken(null);
      setScreen({ kind: "discord", message });
      return;
    }
    setAuthError(message);
    setScreen({ kind: "auth" });
  }, []);

  useEffect(() => {
    api.onSessionEnded((failure) => onSignedOut(failure.message));
    return () => api.onSessionEnded(null);
  }, [onSignedOut]);

  switch (screen.kind) {
    case "loading":
      return <Splash text={IN_DISCORD ? "Signing in with Discord…" : "Summoning the table…"} />;
    case "discord":
      return (
        <div className="splash">
          <p>{screen.message}</p>
          <button type="button" className="btn primary" onClick={() => void signInDiscord()}>
            Sign in with Discord
          </button>
        </div>
      );
    case "offline":
      return (
        <div className="splash">
          <p>{screen.message}</p>
          <button type="button" className="btn primary" onClick={() => void loadMe()}>
            Try again
          </button>
        </div>
      );
    case "auth":
      return <AuthScreen onAuthed={onAuthed} initialError={authError} />;
    case "menu":
      return me ? (
        <WelcomeScreen
          me={me}
          onMeChange={setMe}
          onEnterLobby={enterLobby}
          onLogout={onLogout}
          canLogout={!IN_DISCORD}
          onVoiceTable={instancePass ? () => joinVoiceTable(instancePass) : null}
          notice={notice}
          onDismissNotice={() => setNotice(null)}
        />
      ) : null;
    case "game":
      return (
        <Suspense fallback={<Splash text="Setting up the table…" />}>
          <GameScreen
            key={screen.lobbyId}
            lobbyId={screen.lobbyId}
            me={me}
            onMeChange={setMe}
            onExit={exitLobby}
            onSignedOut={() => onSignedOut()}
          />
        </Suspense>
      );
  }
}
